import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { AuthProvider, useAuth } from './store/AuthContext';
import AppLayout from './components/layout/AppLayout';
import LoadingSpinner from './components/common/LoadingSpinner';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
// Lazy load pages
const Login                = lazy(() => import('./pages/auth/Login'));
const ForgotPassword       = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword        = lazy(() => import('./pages/auth/ResetPassword'));
const ForceChangePassword  = lazy(() => import('./pages/auth/ForceChangePassword'));
const Dashboard      = lazy(() => import('./pages/dashboard/Dashboard'));
const UserList       = lazy(() => import('./pages/users/UserList'));
const UserForm       = lazy(() => import('./pages/users/UserForm'));
const UserDetail     = lazy(() => import('./pages/users/UserDetail'));
const RolePermissions = lazy(() => import('./pages/users/RolePermissions'));
const LeadList       = lazy(() => import('./pages/leads/LeadList'));
const LeadForm       = lazy(() => import('./pages/leads/LeadForm'));
const LeadDetail     = lazy(() => import('./pages/leads/LeadDetail'));
const DailyQueue     = lazy(() => import('./pages/leads/DailyQueue'));
const LeadTimeline   = lazy(() => import('./pages/leads/LeadTimeline'));
const CallLogs       = lazy(() => import('./pages/leads/CallLogs'));
const CustomerList   = lazy(() => import('./pages/customers/CustomerList'));
const CustomerDetail = lazy(() => import('./pages/customers/CustomerDetail'));
const Communications = lazy(() => import('./pages/communications/Communications'));
const DocumentManager = lazy(() => import('./pages/documents/DocumentManager'));
const Reports        = lazy(() => import('./pages/reports/Reports'));
const ActivityLog    = lazy(() => import('./pages/reports/ActivityLog'));
const EmployeeIncentives = lazy(() => import('./pages/reports/EmployeeIncentives'));
const Feedback         = lazy(() => import('./pages/content/Feedback'));
const ContactMessages  = lazy(() => import('./pages/content/ContactMessages'));
const MatrimonyMessages = lazy(() => import('./pages/matrimony/MatrimonyMessages'));
const FaqManage        = lazy(() => import('./pages/content/FaqManage'));
const SupportSettings  = lazy(() => import('./pages/content/SupportSettings'));
const SuccessStories   = lazy(() => import('./pages/content/SuccessStories'));
const Notifications  = lazy(() => import('./pages/notifications/Notifications'));
const Settings            = lazy(() => import('./pages/settings/Settings'));
const Profile             = lazy(() => import('./pages/settings/Profile'));
const MatrimonyDashboard      = lazy(() => import('./pages/matrimony/MatrimonyDashboard'));
const MatrimonyProfiles       = lazy(() => import('./pages/matrimony/MatrimonyProfiles'));
const MatrimonyProfileCreate  = lazy(() => import('./pages/matrimony/MatrimonyProfileCreate'));
const MatrimonyProfileDetail  = lazy(() => import('./pages/matrimony/MatrimonyProfileDetail'));
const MatrimonyProfileDelete  = lazy(() => import('./pages/matrimony/MatrimonyProfileDelete'));
const MatrimonyDeletedProfiles = lazy(() => import('./pages/matrimony/MatrimonyDeletedProfiles'));
const MasterdataHome          = lazy(() => import('./pages/matrimony/MasterdataHome'));
const MasterdataList          = lazy(() => import('./pages/matrimony/MasterdataList'));
const MatrimonyPlans          = lazy(() => import('./pages/matrimony/MatrimonyPlans'));
const MatrimonyPlanView       = lazy(() => import('./pages/matrimony/MatrimonyPlanView'));
const PhotoApproval                    = lazy(() => import('./pages/matrimony/PhotoApproval'));
const JathagamApproval                 = lazy(() => import('./pages/matrimony/JathagamApproval'));
const ReportedProfiles                 = lazy(() => import('./pages/matrimony/ReportedProfiles'));
const MatrimonyDeleteProfileRequests   = lazy(() => import('./pages/matrimony/MatrimonyDeleteProfileRequests'));
const MatrimonyPaymentOrders           = lazy(() => import('./pages/matrimony/MatrimonyPaymentOrders'));
const MatrimonyRefundRequests          = lazy(() => import('./pages/matrimony/MatrimonyRefundRequests'));
const MatrimonyAccessLog               = lazy(() => import('./pages/matrimony/MatrimonyAccessLog'));
const MatrimonyZeroAccessLog           = lazy(() => import('./pages/matrimony/MatrimonyZeroAccessLog'));
const ProfileMatchScreen               = lazy(() => import('./pages/matrimony/ProfileMatchScreen'));
const CommWhatsAppLog     = lazy(() => import('./pages/communication/CommWhatsAppLog'));
const CommEmailLog        = lazy(() => import('./pages/communication/CommEmailLog'));
const WhatsAppTemplates   = lazy(() => import('./pages/whatsapp/WhatsAppTemplates'));
const WhatsAppBulkSend    = lazy(() => import('./pages/whatsapp/WhatsAppBulkSend'));
const WhatsAppMessageLog  = lazy(() => import('./pages/whatsapp/WhatsAppMessageLog'));
const WhatsAppBulkHistory = lazy(() => import('./pages/whatsapp/WhatsAppBulkHistory'));
const HelpHome            = lazy(() => import('./pages/help/HelpHome'));
const HelpTopic           = lazy(() => import('./pages/help/HelpTopic'));
const NotFound            = lazy(() => import('./pages/NotFound'));

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner fullPage />;
  return user ? children : <Navigate to="/login" replace />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner fullPage />;
  return !user ? children : <Navigate to="/dashboard" replace />;
};

const AppRoutes = () => (
  <Suspense fallback={<LoadingSpinner fullPage />}>
    <Routes>
      {/* Public */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
      <Route path="/change-password" element={<ForceChangePassword />} />

      {/* Private */}
      <Route path="/" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="users" element={<UserList />} />
        <Route path="users/new" element={<UserForm />} />
        <Route path="users/:id" element={<UserDetail />} />
        <Route path="users/:id/edit" element={<UserForm />} />
        <Route path="roles" element={<RolePermissions />} />
        <Route path="leads" element={<LeadList />} />
        <Route path="leads/new" element={<LeadForm />} />
        <Route path="leads/daily-queue" element={<DailyQueue />} />
        {/* #856: Lead Statuses/Priorities/Sources moved into the generic
            Masterdata screen -- redirect any old bookmark/link instead of
            leaving a dead route. */}
        <Route path="leads/settings" element={<Navigate to="/matrimony/masterdata" replace />} />
        <Route path="leads/:id" element={<LeadDetail />} />
        <Route path="leads/:id/edit" element={<LeadForm />} />
        <Route path="leads/:id/timeline" element={<LeadTimeline />} />
        <Route path="call-logs" element={<CallLogs />} />
        <Route path="customers" element={<CustomerList />} />
        <Route path="customers/:id" element={<CustomerDetail />} />
        <Route path="communications/:type/:id" element={<Communications />} />
        <Route path="documents" element={<DocumentManager />} />
        <Route path="reports" element={<Reports />} />
        <Route path="incentives" element={<EmployeeIncentives />} />
        <Route path="reports/:reportType" element={<Reports />} />
        <Route path="activities" element={<ActivityLog />} />
        <Route path="content/feedback" element={<Feedback />} />
        <Route path="content/contact" element={<ContactMessages />} />
        <Route path="content/faq" element={<FaqManage />} />
        <Route path="content/support-settings" element={<SupportSettings />} />
        <Route path="content/success-stories" element={<SuccessStories />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="settings" element={<Settings />} />
        <Route path="profile" element={<Profile />} />
        <Route path="matrimony/dashboard" element={<MatrimonyDashboard />} />
        <Route path="matrimony/profiles" element={<MatrimonyProfiles />} />
        <Route path="matrimony/profiles/new" element={<MatrimonyProfileCreate />} />
        <Route path="matrimony/profiles/deleted" element={<MatrimonyDeletedProfiles />} />
        <Route path="matrimony/archive" element={<MatrimonyProfiles archiveMode />} />
        <Route path="matrimony/profiles/:id/delete" element={<MatrimonyProfileDelete />} />
        <Route path="matrimony/profiles/:id" element={<MatrimonyProfileDetail />} />
        <Route path="matrimony/masterdata" element={<MasterdataHome />} />
        <Route path="matrimony/masterdata/:key" element={<MasterdataList />} />
        <Route path="matrimony/plans" element={<MatrimonyPlans />} />
        <Route path="matrimony/plans/:id" element={<MatrimonyPlanView />} />
        <Route path="matrimony/photo-approval" element={<PhotoApproval />} />
        <Route path="matrimony/jathagam-approval" element={<JathagamApproval />} />
        <Route path="matrimony/reported-profiles" element={<ReportedProfiles />} />
        <Route path="matrimony/delete-requests" element={<MatrimonyDeleteProfileRequests />} />
        <Route path="matrimony/payments" element={<MatrimonyPaymentOrders />} />
        <Route path="matrimony/refund-requests" element={<MatrimonyRefundRequests />} />
        <Route path="matrimony/profiles/:id/access-log" element={<MatrimonyAccessLog />} />
        <Route path="matrimony/zero-access-log" element={<MatrimonyZeroAccessLog />} />
        <Route path="matrimony/profile-match" element={<ProfileMatchScreen />} />
        <Route path="communication/whatsapp-log" element={<CommWhatsAppLog />} />
        <Route path="communication/email-log" element={<CommEmailLog />} />
        <Route path="messages" element={<MatrimonyMessages />} />
        <Route path="whatsapp/templates" element={<WhatsAppTemplates />} />
        <Route path="whatsapp/bulk-send" element={<WhatsAppBulkSend />} />
        <Route path="whatsapp/message-log" element={<WhatsAppMessageLog />} />
        <Route path="whatsapp/bulk-history" element={<WhatsAppBulkHistory />} />
        <Route path="help" element={<HelpHome />} />
        <Route path="help/:topic" element={<HelpTopic />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  </Suspense>
);

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <ToastContainer
          position="top-right"
          autoClose={3500}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </BrowserRouter>
    </AuthProvider>
  );
}
